import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Country, State } from 'country-state-city';
import { createAccountCustomer, uploadMenuFiles } from '../core/_requests';
import { useAccountCustomers } from '../core/useAccountCustomers';
import { X, Upload, Plus, Trash2, CheckCircle2 } from 'lucide-react';
import SearchableSelect from '../../../shared/components/SearchableSelect'
import './CustomerSelectorField.css';

const emptyContact = (id) => ({
  id, salutation: '', firstName: '', lastName: '', email: '', phone: '', mobile: '', skype: '', designation: '', department: '',
});

type CustomerSelectorFieldProps = {
  value: string | number;
  onChange: (id: string, name: string) => void;
  width?: string;
  customersHook?: ReturnType<typeof useAccountCustomers>;
  className?: string;
};

const CustomerSelectorField = ({
  value,
  onChange,
  width = '400px',
  customersHook,
  className,
}: CustomerSelectorFieldProps) => {
  const internalHook = useAccountCustomers();
  const { customers, loading, loaded, error, loadCustomers, refreshCustomers } = customersHook || internalHook;
  const showEmpty = loaded && !loading && !error && customers.length === 0;
  const selectValue = value === null || value === undefined ? '' : String(value);

  const [showPanel, setShowPanel] = useState(false);
  const [toast, setToast] = useState('');

  const openPanel = () => setShowPanel(true);
  const closePanel = () => setShowPanel(false);

  useEffect(() => {
    if (!loaded && !loading) {
      void loadCustomers();
    }
  }, [loaded, loading, loadCustomers]);

  const handleCreated = useCallback((newCustomer) => {
    closePanel();
    if (refreshCustomers) void refreshCustomers();
    onChange(String(newCustomer.id), newCustomer.name || '');
    setToast(`Customer "${newCustomer.name}" created!`);
    setTimeout(() => setToast(''), 3000);
  }, [onChange, refreshCustomers]);

  return (
    <>
      <div className="csf-wrapper" style={{ width }}>
        <div className="csf-select-wrap">
          <SearchableSelect
            className={className || "sel w-100"}
            style={{ paddingRight: '40px' }}
            value={selectValue}
            onChange={(e) => {
              const id = e.target.value;
              if (id === '__new__') {
                openPanel();
                e.currentTarget.value = selectValue;
                return;
              }
              const selectedCustomer = customers.find((c: any) => String(c.id) === String(id));
              const displayName = selectedCustomer
                ? (selectedCustomer.displayName || selectedCustomer.companyName || selectedCustomer.name || selectedCustomer.firstName || '')
                : '';
              onChange(id, displayName);
            }}
          >
            <option value="" disabled hidden>Select or add a customer</option>
            {loading && <option disabled>Loading customers...</option>}
            {error && <option disabled>Failed to load customers</option>}
            {showEmpty && <option disabled>No customers found</option>}
            {customers.filter((c: any) => c.status !== 'inactive').map((c) => (
              <option key={c.id} value={c.id}>{c.displayName || c.companyName || c.name || c.firstName || 'Customer'}</option>
            ))}
            <option value="__new__" style={{ fontWeight: 700 }}>Add new customer...</option>
          </SearchableSelect>
        </div>
      </div>

      {showPanel && ReactDOM.createPortal(
        <AddCustomerPanel onClose={closePanel} onCreated={handleCreated} />, document.body
      )}

      {toast && ReactDOM.createPortal(
        <div className="csf-success-toast"><CheckCircle2 className="w-4 h-4" />{toast}</div>, document.body
      )}
    </>
  );
};

/* ═══════════════════════════════════════════════════════
   AddCustomerPanel — Full New Customer form in a slide-in
   ═══════════════════════════════════════════════════════ */

const AddCustomerPanel = ({ onClose, onCreated }) => {
  const formRef = useRef<HTMLDivElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('Other Details');
  const [showMore, setShowMore] = useState(false);
  const [contacts, setContacts] = useState([emptyContact(1)]);
  const [nextId, setNextId] = useState(2);
  const [showExtra, setShowExtra] = useState(false);
  const [currencyCode, setCurrencyCode] = useState('INR');
  const [billingCountry, setBillingCountry] = useState('IN');
  const [billingState, setBillingState] = useState('');
  const [shippingCountry, setShippingCountry] = useState('IN');
  const [shippingState, setShippingState] = useState('');
  const [docs, setDocs] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);

  const countries = Country.getAllCountries();
  const bStates = State.getStatesOfCountry(billingCountry);
  const sStates = State.getStatesOfCountry(shippingCountry);
  const tabs = ['Other Details', 'Address', 'Contact Persons', 'Remarks'];

  const inputClass = "w-full px-3.5 py-2 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 transition-all hover:border-slate-300";
  const labelClass = "text-[13px] font-bold text-slate-700";
  const smallLabelClass = "block text-[11px] font-bold text-slate-600 mb-1.5";

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const addContact = () => { setContacts(p => [...p, emptyContact(nextId)]); setNextId(n => n + 1); };
  const removeContact = (id) => { if (contacts.length > 1) setContacts(p => p.filter(c => c.id !== id)); };
  const updateContact = (id, field, val) => {
    let cleanVal = val;
    if (field === 'phone' || field === 'mobile') {
      cleanVal = val.replace(/\D/g, '').slice(0, 10);
    }
    setContacts(p => p.map(c => c.id === id ? { ...c, [field]: cleanVal } : c));
  };

  const handleDocChange = async (e) => {
    const files = Array.from(e.target.files || []) as File[];
    if (!files.length) return;
    setUploading(true); setUploadErr(null);
    try { const u = await uploadMenuFiles('customers', files); setDocs(p => [...p, ...u]); }
    catch (err) { setUploadErr('Upload failed.'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const root = formRef.current;
      const gv = (n: string) => (
        root?.querySelector<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(`[name="${n}"]`)
      )?.value?.trim() || '';
      const gc = (n: string) => (
        root?.querySelector<HTMLInputElement>(`input[name="${n}"]`)
      )?.checked ?? false;
      const gr = (n: string) => (
        root?.querySelector<HTMLInputElement>(`input[name="${n}"]:checked`)
      )?.value || 'Business';

      const firstName = gv('firstName'), lastName = gv('lastName');
      const displayName = gv('displayName') || `${firstName} ${lastName}`.trim() || 'New Customer';
      const ob = Number(gv('openingBalance') || 0);

      const filteredContacts = contacts
        .map(c => ({ salutation: c.salutation, firstName: c.firstName, lastName: c.lastName, email: c.email, phone: c.phone, mobile: c.mobile, skype: c.skype, designation: c.designation, department: c.department }))
        .filter(c => Object.values(c).some(v => String(v || '').trim() !== ''));

      // Validate email format
      const email = gv('email');
      if (email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          alert('Please enter a valid email address.');
          setSaving(false);
          return;
        }
      }

      // Validate contact emails
      for (const contact of filteredContacts) {
        if (contact.email) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(contact.email)) {
            alert(`Please enter a valid email address for contact: ${contact.firstName || ''} ${contact.lastName || ''}`);
            setSaving(false);
            return;
          }
        }
      }

      const payload = {
        customerType: gr('customerType'), salutation: gv('salutation'), firstName, lastName,
        companyName: gv('companyName'), displayName, email: gv('email'),
        workPhone: gv('workPhone'), mobile: gv('mobile'), language: gv('language'), remarks: gv('remarks'),
        otherDetails: {
          pan: gv('pan'), currency: currencyCode, accountsReceivable: gv('accountsReceivable'),
          openingBalance: Number.isFinite(ob) ? ob : 0, paymentTerms: gv('paymentTerms'),
          enablePortal: gc('enablePortal'), documents: docs.length ? JSON.stringify(docs) : undefined,
          website: gv('website'), department: gv('department'), designation: gv('designation'),
          twitter: gv('twitter'), skype: gv('skype'), facebook: gv('facebook'),
        },
        address: {
          billingAttention: gv('billingAttention'), billingCountry, billingStreet1: gv('billingStreet1'),
          billingStreet2: gv('billingStreet2'), billingCity: gv('billingCity'), billingState,
          billingPincode: gv('billingPincode'), billingPhone: gv('billingPhone'), billingFax: gv('billingFax'),
          shippingAttention: gv('shippingAttention'), shippingCountry, shippingStreet1: gv('shippingStreet1'),
          shippingStreet2: gv('shippingStreet2'), shippingCity: gv('shippingCity'), shippingState,
          shippingPincode: gv('shippingPincode'), shippingPhone: gv('shippingPhone'), shippingFax: gv('shippingFax'),
        },
        contacts: filteredContacts,
      };

      const result = await createAccountCustomer(payload) as any;
      const created = {
        id: result.id, name: displayName, company: payload.companyName,
        email: payload.email, phone: payload.workPhone || payload.mobile, receivables: '$0.00',
      };
      onCreated(created);
    } catch (err) { console.error('Failed to create customer:', err); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-10000 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200 relative" onClick={(e) => e.stopPropagation()}>
        
        {saving && (
          <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="w-8 h-8 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin"></div>
          </div>
        )}

        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Add New Customer</h2>
            <p className="text-xs font-medium text-slate-500 mt-0.5">Create a new customer profile quickly</p>
          </div>
          <button className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors" onClick={onClose}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8" ref={formRef}>
          <div className="max-w-3xl space-y-6">
            
            {/* Customer Type */}
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="md:w-40 shrink-0"><label className={labelClass}>Customer Type</label></div>
              <div className="flex-1 flex gap-6">
                <label className="flex items-center gap-2 text-sm cursor-pointer group"><input type="radio" name="customerType" value="Business" defaultChecked className="w-4 h-4 text-blue-600 border-slate-300 focus:ring-blue-500" /><span className="group-hover:text-blue-600 text-slate-700 transition-colors">Business</span></label>
                <label className="flex items-center gap-2 text-sm cursor-pointer group"><input type="radio" name="customerType" value="Individual" className="w-4 h-4 text-blue-600 border-slate-300 focus:ring-blue-500" /><span className="group-hover:text-blue-600 text-slate-700 transition-colors">Individual</span></label>
              </div>
            </div>

            {/* Primary Contact */}
            <div className="flex flex-col md:flex-row md:items-start gap-4">
              <div className="md:w-40 shrink-0 pt-2.5"><label className={labelClass}>Primary Contact</label></div>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-4">
                <div className="md:col-span-3">
                  <SearchableSelect className={inputClass} name="salutation"><option value="">Salutation</option><option value="Mr.">Mr.</option><option value="Mrs.">Mrs.</option><option value="Ms.">Ms.</option><option value="Miss">Miss</option><option value="Dr.">Dr.</option></SearchableSelect>
                </div>
                <div className="md:col-span-4">
                  <input type="text" className={inputClass} name="firstName" placeholder="First Name" />
                </div>
                <div className="md:col-span-5">
                  <input type="text" className={inputClass} name="lastName" placeholder="Last Name" />
                </div>
              </div>
            </div>

            {/* Company Name */}
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="md:w-40 shrink-0"><label className={labelClass}>Company Name</label></div>
              <div className="flex-1"><input type="text" className={inputClass} name="companyName" placeholder="Company Name" /></div>
            </div>

            {/* Display Name */}
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="md:w-40 shrink-0"><label className={labelClass}>Display Name <span className="text-red-500">*</span></label></div>
              <div className="flex-1"><input type="text" className={inputClass} name="displayName" placeholder="Type to add" /></div>
            </div>

            {/* Email Address */}
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="md:w-40 shrink-0"><label className={labelClass}>Email Address</label></div>
              <div className="flex-1"><input type="email" className={inputClass} name="email" placeholder="Email" /></div>
            </div>

            {/* Phone */}
            <div className="flex flex-col md:flex-row md:items-start gap-4">
              <div className="md:w-40 shrink-0 pt-2.5"><label className={labelClass}>Phone</label></div>
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-4"><SearchableSelect className={inputClass}><option>+91</option></SearchableSelect></div>
                  <div className="col-span-8"><input type="text" className={inputClass} name="workPhone" placeholder="Work Phone" maxLength={10} onInput={e => e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '').slice(0, 10)} /></div>
                </div>
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-4"><SearchableSelect className={inputClass}><option>+91</option></SearchableSelect></div>
                  <div className="col-span-8"><input type="text" className={inputClass} name="mobile" placeholder="Mobile" maxLength={10} onInput={e => e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '').slice(0, 10)} /></div>
                </div>
              </div>
            </div>

            {/* Language */}
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="md:w-40 shrink-0"><label className={labelClass}>Language</label></div>
              <div className="flex-1 max-w-sm"><SearchableSelect className={inputClass} name="language"><option>English</option><option>Hindi</option><option>Spanish</option><option>French</option></SearchableSelect></div>
            </div>

          </div>

          {/* Tabs Navigation */}
          <div className="flex gap-2 mt-10 mb-6 border-b border-slate-200">
            {tabs.map(t => (
              <button 
                key={t} type="button" 
                className={`px-4 py-2.5 text-sm font-bold border-b-2 transition-colors -mb-px ${activeTab === t ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`}
                onClick={() => setActiveTab(t)}
              >
                {t}
              </button>
            ))}
          </div>

          {/* Tabs Content */}
          <div className="min-h-62.5">
            {activeTab === 'Other Details' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-5">
                  <div><label className={smallLabelClass}>PAN</label><input type="text" className={inputClass} name="pan" /></div>
                  <div>
                    <label className={smallLabelClass}>Currency</label>
                    <SearchableSelect className={inputClass} name="currency" value={currencyCode} onChange={e => setCurrencyCode(e.target.value)}>
                      <option value="INR">INR - Indian Rupee</option><option value="USD">USD - US Dollar</option><option value="EUR">EUR - Euro</option><option value="GBP">GBP - British Pound</option>
                    </SearchableSelect>
                  </div>
                  <div>
                    <label className={smallLabelClass}>Opening Balance</label>
                    <div className="relative">
                      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-medium text-sm">{currencyCode}</span>
                      <input 
                        type="number" 
                        className={`${inputClass} pl-12`} 
                        name="openingBalance" 
                        min="0"
                        onKeyDown={e => {
                          if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                            e.preventDefault();
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div><label className={smallLabelClass}>Payment Terms</label><SearchableSelect className={inputClass} name="paymentTerms"><option>Due on Receipt</option></SearchableSelect></div>
                  <div>
                    <label className={smallLabelClass}>Enable Portal?</label>
                    <label className="flex items-center gap-2 cursor-pointer mt-2 text-sm text-slate-700 font-medium"><input type="checkbox" name="enablePortal" className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-slate-300" /> Allow portal access</label>
                  </div>
                </div>
                <div className="space-y-5">
                  <div>
                    <label className={smallLabelClass}>Documents</label>
                    <input ref={docInputRef} type="file" multiple onChange={handleDocChange} hidden />
                    <button type="button" className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 hover:border-slate-300 rounded-xl text-sm font-bold text-slate-700 transition-colors shadow-sm" onClick={() => docInputRef.current?.click()} disabled={uploading}>
                      <Upload className="w-4 h-4 text-slate-400" /> {uploading ? 'Uploading...' : 'Upload File'}
                    </button>
                    {uploadErr && <p className="text-[11px] text-red-500 mt-2">{uploadErr}</p>}
                    {docs.length > 0 && <div className="mt-2 text-[11px] text-slate-500">{docs.map(d => <div key={d.fileName}>{d.originalName}</div>)}</div>}
                  </div>
                  {showMore && (
                    <div className="space-y-5 animate-in fade-in slide-in-from-top-4 duration-300">
                      <div><label className={smallLabelClass}>Website URL</label><input type="text" className={inputClass} name="website" placeholder="www.example.com" /></div>
                      <div><label className={smallLabelClass}>Department</label><input type="text" className={inputClass} name="department" /></div>
                      <div><label className={smallLabelClass}>Designation</label><input type="text" className={inputClass} name="designation" /></div>
                      <div><label className={smallLabelClass}>Twitter / X</label><input type="text" className={inputClass} name="twitter" /></div>
                      <div><label className={smallLabelClass}>Skype</label><input type="text" className={inputClass} name="skype" /></div>
                      <div><label className={smallLabelClass}>Facebook</label><input type="text" className={inputClass} name="facebook" /></div>
                    </div>
                  )}
                  <div>
                    <button type="button" className="text-xs font-bold text-blue-600 hover:text-blue-700" onClick={() => setShowMore(!showMore)}>{showMore ? 'Show less details' : 'Add more details'}</button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'Address' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2"><span className="w-1.5 h-4 bg-blue-500 rounded-full"></span> Billing Address</h3>
                  <div><label className={smallLabelClass}>Attention</label><input type="text" className={inputClass} name="billingAttention" /></div>
                  <div>
                    <label className={smallLabelClass}>Country</label>
                    <SearchableSelect className={inputClass} value={billingCountry} onChange={e => { setBillingCountry(e.target.value); setBillingState(''); }}>{countries.map(c => <option key={c.isoCode} value={c.isoCode}>{c.name}</option>)}</SearchableSelect>
                  </div>
                  <div><label className={smallLabelClass}>Address</label><textarea className={`${inputClass} mb-3`} name="billingStreet1" placeholder="Street 1" rows={2}></textarea><textarea className={inputClass} name="billingStreet2" placeholder="Street 2" rows={2}></textarea></div>
                  <div><label className={smallLabelClass}>City</label><input type="text" className={inputClass} name="billingCity" /></div>
                  <div>
                    <label className={smallLabelClass}>State</label>
                    <SearchableSelect className={inputClass} value={billingState} onChange={e => setBillingState(e.target.value)}><option value="">Select</option>{bStates.map(s => <option key={s.isoCode} value={s.isoCode}>{s.name}</option>)}</SearchableSelect>
                  </div>
                  <div><label className={smallLabelClass}>Pin Code</label><input type="text" className={inputClass} name="billingPincode" maxLength={10} onInput={e => e.currentTarget.value = e.currentTarget.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 10)} /></div>
                  <div><label className={smallLabelClass}>Phone</label><div className="grid grid-cols-12 gap-2"><div className="col-span-4"><SearchableSelect className={inputClass}><option>+91</option></SearchableSelect></div><div className="col-span-8"><input type="text" className={inputClass} name="billingPhone" maxLength={10} onInput={e => e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '').slice(0, 10)} /></div></div></div>
                  <div><label className={smallLabelClass}>Fax</label><input type="text" className={inputClass} name="billingFax" maxLength={10} onInput={e => e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '').slice(0, 10)} /></div>
                </div>
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2"><span className="w-1.5 h-4 bg-emerald-500 rounded-full"></span> Shipping Address</h3>
                  <div><label className={smallLabelClass}>Attention</label><input type="text" className={inputClass} name="shippingAttention" /></div>
                  <div>
                    <label className={smallLabelClass}>Country</label>
                    <SearchableSelect className={inputClass} value={shippingCountry} onChange={e => { setShippingCountry(e.target.value); setShippingState(''); }}>{countries.map(c => <option key={c.isoCode} value={c.isoCode}>{c.name}</option>)}</SearchableSelect>
                  </div>
                  <div><label className={smallLabelClass}>Address</label><textarea className={`${inputClass} mb-3`} name="shippingStreet1" placeholder="Street 1" rows={2}></textarea><textarea className={inputClass} name="shippingStreet2" placeholder="Street 2" rows={2}></textarea></div>
                  <div><label className={smallLabelClass}>City</label><input type="text" className={inputClass} name="shippingCity" /></div>
                  <div>
                    <label className={smallLabelClass}>State</label>
                    <SearchableSelect className={inputClass} value={shippingState} onChange={e => setShippingState(e.target.value)}><option value="">Select</option>{sStates.map(s => <option key={s.isoCode} value={s.isoCode}>{s.name}</option>)}</SearchableSelect>
                  </div>
                  <div><label className={smallLabelClass}>Pin Code</label><input type="text" className={inputClass} name="shippingPincode" maxLength={10} onInput={e => e.currentTarget.value = e.currentTarget.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 10)} /></div>
                  <div><label className={smallLabelClass}>Phone</label><div className="grid grid-cols-12 gap-2"><div className="col-span-4"><SearchableSelect className={inputClass}><option>+91</option></SearchableSelect></div><div className="col-span-8"><input type="text" className={inputClass} name="shippingPhone" maxLength={10} onInput={e => e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '').slice(0, 10)} /></div></div></div>
                  <div><label className={smallLabelClass}>Fax</label><input type="text" className={inputClass} name="shippingFax" maxLength={10} onInput={e => e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '').slice(0, 10)} /></div>
                </div>
              </div>
            )}

            {activeTab === 'Contact Persons' && (
              <div>
                <div className="overflow-x-auto border border-slate-200 rounded-xl mb-4">
                  <table className="w-full text-left min-w-175">
                    <thead className="bg-slate-50 border-b border-slate-200 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                      <tr>
                        <th className="px-4 py-3">Salutation</th><th className="px-4 py-3">First Name</th><th className="px-4 py-3">Last Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3">Mobile</th>
                        <th className="px-4 py-3 w-12 text-center"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {contacts.map(c => (
                        <tr key={c.id} className="hover:bg-slate-50/50">
                          <td className="p-3"><SearchableSelect className={inputClass} value={c.salutation} onChange={e => updateContact(c.id, 'salutation', e.target.value)}><option value="">Select</option><option value="Mr.">Mr.</option><option value="Mrs.">Mrs.</option><option value="Ms.">Ms.</option></SearchableSelect></td>
                          <td className="p-3"><input type="text" className={inputClass} placeholder="First Name" value={c.firstName} onChange={e => updateContact(c.id, 'firstName', e.target.value)} /></td>
                          <td className="p-3"><input type="text" className={inputClass} placeholder="Last Name" value={c.lastName} onChange={e => updateContact(c.id, 'lastName', e.target.value)} /></td>
                          <td className="p-3"><input type="email" className={inputClass} placeholder="Email" value={c.email} onChange={e => updateContact(c.id, 'email', e.target.value)} /></td>
                          <td className="p-3"><input type="text" className={inputClass} placeholder="Phone" value={c.phone} maxLength={10} onChange={e => updateContact(c.id, 'phone', e.target.value)} /></td>
                          <td className="p-3"><input type="text" className={inputClass} placeholder="Mobile" value={c.mobile} maxLength={10} onChange={e => updateContact(c.id, 'mobile', e.target.value)} /></td>
                          <td className="p-3 text-center">
                            {contacts.length > 1 && <button type="button" className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" onClick={() => removeContact(c.id)}><Trash2 className="w-4 h-4" /></button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button type="button" className="text-xs font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1.5 bg-blue-50 px-3 py-2 rounded-xl" onClick={addContact}><Plus className="w-3.5 h-3.5" /> Add Contact Person</button>
              </div>
            )}

            {activeTab === 'Remarks' && (
              <div>
                <label className={smallLabelClass}>Remarks (For Internal Use)</label>
                <textarea className={inputClass} name="remarks" rows={5} placeholder="Add remarks..."></textarea>
              </div>
            )}

          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3 shrink-0 rounded-b-2xl">
          <button className="px-5 py-2.5 text-sm font-bold text-slate-600 hover:text-slate-800 hover:bg-slate-200 bg-slate-100 border border-slate-200 rounded-xl transition-colors" type="button" onClick={onClose}>Cancel</button>
          <button className="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow hover:-translate-y-0.5 transition-all disabled:opacity-50" type="button" onClick={handleSave} disabled={saving || uploading}>
            {saving ? 'Saving...' : 'Save Customer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerSelectorField;

